import { defineStore } from "pinia";
import type { ShippingMethod } from "@/services/marketplace/shippingMethod";
import type { PaymentMethod } from "@/services/marketplace/paymentMethod";
import type { Address_CreationData } from "@/services/addresses";

const STORAGE_KEY = "anonymous-cart-preferences";

type StoredPreferences = {
  selectedShippingMethod: ShippingMethod | null;
  selectedPaymentMethod: PaymentMethod | null;
  zasilkovnaAddress: Address_CreationData | null;
};

const loadFromStorage = (): StoredPreferences => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : { selectedShippingMethod: null, selectedPaymentMethod: null, zasilkovnaAddress: null };
  } catch {
    return { selectedShippingMethod: null, selectedPaymentMethod: null, zasilkovnaAddress: null };
  }
};

const useAnonymousCartPreferencesStore = defineStore("anonymousCartPreferences", () => {
  const stored = loadFromStorage();
  const selectedShippingMethod = ref<ShippingMethod | null>(stored.selectedShippingMethod);
  const selectedPaymentMethod = ref<PaymentMethod | null>(stored.selectedPaymentMethod);
  const zasilkovnaAddress = ref<Address_CreationData | null>(stored.zasilkovnaAddress);
  return { selectedShippingMethod, selectedPaymentMethod, zasilkovnaAddress };
});

export function useAnonymousCartPreferences() {
  const store = useAnonymousCartPreferencesStore();

  const persist = (): void => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        selectedShippingMethod: store.selectedShippingMethod,
        selectedPaymentMethod: store.selectedPaymentMethod,
        zasilkovnaAddress: store.zasilkovnaAddress,
      })
    );
  };

  const setShippingMethod = (method: ShippingMethod): void => {
    store.selectedShippingMethod = method;
    persist();
  };

  const setPaymentMethod = (method: PaymentMethod): void => {
    store.selectedPaymentMethod = method;
    persist();
  };

  const setZasilkovnaAddress = (address: Address_CreationData): void => {
    store.zasilkovnaAddress = address;
    persist();
  };

  const clearZasilkovnaAddress = (): void => {
    store.zasilkovnaAddress = null;
    persist();
  };

  const clearPreferences = (): void => {
    store.selectedShippingMethod = null;
    store.selectedPaymentMethod = null;
    store.zasilkovnaAddress = null;
    localStorage.removeItem(STORAGE_KEY);
  };

  return {
    selectedShippingMethod: computed(() => store.selectedShippingMethod),
    selectedPaymentMethod: computed(() => store.selectedPaymentMethod),
    zasilkovnaAddress: computed(() => store.zasilkovnaAddress),
    setShippingMethod,
    setPaymentMethod,
    setZasilkovnaAddress,
    clearZasilkovnaAddress,
    clearPreferences,
  };
}
